import { useEffect, useState } from "react";
import {
    ResponsiveContainer, AreaChart, Area, BarChart, Bar,
    XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";
import { api } from "../api";
import { Panel, PageHead, Loading, LoadError } from "../ui";

const AXIS = { stroke: "#1D322E", tick: { fill: "#A3B8B0", fontSize: 11 } };
const TOOLTIP = {
    contentStyle: { backgroundColor: "#12201D", border: "1px solid #1D322E", borderRadius: 8, fontSize: 12 },
    labelStyle: { color: "#F7F5EF" },
    itemStyle: { color: "#10B981" },
    cursor: { fill: "rgba(16,185,129,0.06)" },
};

const ChartCard = ({ title, data, testid, color = "#10B981" }) => (
    <Panel className="p-5">
        <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">{title}</h2>
        <div className="mt-4 h-56" data-testid={testid}>
            {(!data || data.length === 0) ? (
                <div className="flex h-full items-center justify-center text-sm text-[#64716D]" data-testid={`${testid}-empty`}>
                    No data yet
                </div>
            ) : (
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#1D322E" vertical={false} />
                        <XAxis dataKey="label" tickLine={false} {...AXIS} />
                        <YAxis allowDecimals={false} tickLine={false} {...AXIS} />
                        <Tooltip {...TOOLTIP} />
                        <Bar dataKey="count" fill={color} radius={[4, 4, 0, 0]} maxBarSize={42} />
                    </BarChart>
                </ResponsiveContainer>
            )}
        </div>
    </Panel>
);

export const AdminAnalytics = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/admin/analytics")
            .then((res) => setData(res.data))
            .catch(() => setError("Could not load analytics. Please refresh."));
    }, []);

    if (error) return <LoadError message={error} />;
    if (!data) return <Loading />;

    const short = (items) =>
        (items || []).map((i) => ({ ...i, label: i.label.length > 14 ? `${i.label.slice(0, 13)}…` : i.label }));

    const cards = [
        ["Total cases", data.totals.total],
        ["New", data.totals.new],
        ["Active", data.totals.active],
        ["Closed", data.totals.closed],
        ["Consultation pending", data.totals.consultation_pending],
        ["Treatment planning", data.totals.treatment_planning],
        ["Travel planning", data.totals.travel_planning],
        ["Follow-up", data.totals.follow_up],
    ];

    return (
        <div data-testid="admin-analytics-page">
            <PageHead
                title="Operational Analytics"
                subtitle="Real metrics from actual case data — nothing here is estimated."
            />

            <div className="grid grid-cols-2 xl:grid-cols-4 gap-4" data-testid="analytics-totals">
                {cards.map(([label, value]) => (
                    <Panel key={label} className="p-5">
                        <p className="text-xs font-semibold uppercase tracking-wider text-[#64716D]">{label}</p>
                        <p className="mt-2 text-3xl font-bold text-[#F7F5EF]">{value}</p>
                    </Panel>
                ))}
            </div>

            <Panel className="mt-6 p-5">
                <h2 className="text-sm font-semibold uppercase tracking-wider text-emerald-400">Cases over time</h2>
                <div className="mt-4 h-64" data-testid="analytics-trend-chart">
                    {data.totals.total === 0 ? (
                        <div className="flex h-full items-center justify-center text-sm text-[#64716D]" data-testid="analytics-trend-empty">
                            No data yet
                        </div>
                    ) : (
                        <ResponsiveContainer width="100%" height="100%">
                            <AreaChart data={data.by_month} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
                                <defs>
                                    <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#10B981" stopOpacity={0.35} />
                                        <stop offset="100%" stopColor="#10B981" stopOpacity={0.02} />
                                    </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#1D322E" vertical={false} />
                                <XAxis dataKey="label" tickLine={false} {...AXIS} />
                                <YAxis allowDecimals={false} tickLine={false} {...AXIS} />
                                <Tooltip {...TOOLTIP} />
                                <Area type="monotone" dataKey="count" stroke="#10B981" strokeWidth={2} fill="url(#trendFill)" />
                            </AreaChart>
                        </ResponsiveContainer>
                    )}
                </div>
            </Panel>

            <div className="mt-6 grid lg:grid-cols-2 gap-6">
                <ChartCard title="By status" data={short(data.by_status)} testid="analytics-status-chart" />
                <ChartCard title="By country" data={short(data.by_country)} testid="analytics-country-chart" color="#D4AF37" />
                <ChartCard title="By specialty" data={short(data.by_specialty)} testid="analytics-specialty-chart" color="#06B6D4" />
                <ChartCard title="By destination" data={short(data.by_destination)} testid="analytics-destination-chart" color="#789B83" />
                <ChartCard title="By source" data={short(data.by_source)} testid="analytics-source-chart" color="#F59E0B" />
                <ChartCard title="By coordinator" data={short(data.by_coordinator)} testid="analytics-coordinator-chart" color="#F43F5E" />
            </div>
        </div>
    );
};
